import { Budget } from '@/types';
import { BudgetCard } from './BudgetCard';
import { NoBudgetsEmpty } from '@/components/ui/EmptyState';
import { LoadingCard } from '@/components/ui/Loading';

interface BudgetListProps {
  budgets: Budget[];
  isLoading?: boolean;
  onCreateClick?: () => void;
  onBudgetClick?: (budget: Budget) => void;
}

export function BudgetList({
  budgets,
  isLoading,
  onCreateClick,
  onBudgetClick,
}: BudgetListProps) {
  if (isLoading) {
    return <LoadingCard className="min-h-[200px]" />;
  }

  if (budgets.length === 0) {
    return <NoBudgetsEmpty onCreateClick={onCreateClick} />;
  }

  return (
    <div className="grid gap-4">
      {budgets.map((budget) => (
        <BudgetCard
          key={budget.id}
          budget={budget}
          onClick={onBudgetClick ? () => onBudgetClick(budget) : undefined}
        />
      ))}
    </div>
  );
}

interface GroupedBudgetListProps {
  budgets: Budget[];
  isLoading?: boolean;
  onCreateClick?: () => void;
  showChildSummary?: boolean;
}

export function GroupedBudgetList({
  budgets,
  isLoading,
  onCreateClick,
  showChildSummary = false,
}: GroupedBudgetListProps) {
  if (isLoading) {
    return <LoadingCard className="min-h-[200px]" />;
  }

  if (budgets.length === 0) {
    return <NoBudgetsEmpty onCreateClick={onCreateClick} />;
  }

  const obligations = budgets.filter((b) => b.type === 'OBLIGATION');
  const discretionary = budgets.filter((b) => b.type === 'DISCRETIONARY');

  const renderCard = (budget: Budget) => (
    <BudgetCard
      key={budget.id}
      budget={budget}
      showChildSummary={showChildSummary}
    />
  );

  return (
    <div className="space-y-6">
      {obligations.length > 0 && (
        <section>
          <h2 className="mb-3 text-sm font-medium text-muted-foreground uppercase tracking-wide">
            Obligations ({obligations.length})
          </h2>
          <div className="grid gap-3">
            {obligations.map(renderCard)}
          </div>
        </section>
      )}

      {discretionary.length > 0 && (
        <section>
          <h2 className="mb-3 text-sm font-medium text-muted-foreground uppercase tracking-wide">
            Discretionary ({discretionary.length})
          </h2>
          <div className="grid gap-3">
            {discretionary.map(renderCard)}
          </div>
        </section>
      )}
    </div>
  );
}
